import { useEffect, useState, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { formatReference } from '@skjv/common';
import { CloseIcon, ShareIcon, TrashIcon, ChevronRightIcon } from '@components/icons';

/**
 * Note editor panel - appears as a bottom drawer similar to Strong's panel.
 * Allows creating/editing notes for verses.
 */
export const NoteEditor = observer(function NoteEditor() {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState<'entering' | 'exiting' | 'visible'>(
    'exiting',
  );
  const [noteText, setNoteText] = useState('');
  const [isShowingVerse, setIsShowingVerse] = useState(false);
  const [verseText, setVerseText] = useState('');
  const saveTimerRef = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isVisible = bibleState.noteEditorVisible;
  const location = bibleState.interactionLocation;
  const existingNote = bibleState.noteAtInteractionLocation;

  // Handle visibility changes with animation
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setAnimationState('entering');
      const timer = setTimeout(() => setAnimationState('visible'), 500);
      return () => clearTimeout(timer);
    } else {
      setAnimationState('exiting');
      const timer = setTimeout(() => setShouldRender(false), 350);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Load existing note and verse text when location changes
  useEffect(() => {
    if (location && isVisible) {
      // Load existing note
      setNoteText(existingNote?.noteText ?? '');

      // Load verse text (walk child nodes to capture spaces and punctuation)
      const verseId = `v-${location.testamentIndex}-${location.bookIndex}-${location.chapterIndex}-${location.verseIndex}`;
      const verseEl = document.getElementById(verseId);
      if (verseEl) {
        let text = '';
        verseEl.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent || '';
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as Element;
            if (!el.classList.contains('verse-num')) {
              text += el.textContent || '';
            }
          }
        });
        setVerseText(text.replace(/\s+/g, ' ').trim());
      }

      // Focus textarea after animation
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 300);
    }
  }, [location, isVisible, existingNote]);

  // Auto-save on change (debounced)
  useEffect(() => {
    if (!isVisible || !location) return;

    // Clear existing timer
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }

    // Set new timer
    if (noteText.trim()) {
      saveTimerRef.current = window.setTimeout(() => {
        bibleState.saveNote(noteText);
      }, 300);
    }

    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, [noteText, isVisible, location]);

  // Save on close
  const handleClose = useCallback(() => {
    if (noteText.trim() && location) {
      bibleState.saveNote(noteText);
    }
    bibleState.hideNoteEditor();
  }, [noteText, location]);

  // Delete note
  const handleDelete = useCallback(() => {
    bibleState.showConfirmDialog({
      title: 'Delete Note',
      message: 'Delete this note?',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: () => {
        bibleState.removeNote();
        setNoteText('');
        bibleState.hideNoteEditor();
      },
    });
  }, []);

  // Share note
  const handleShare = useCallback(async () => {
    if (!location || !noteText.trim()) return;

    const reference = formatReference(location);
    const fullText = `${reference}\n\n${noteText}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Note: ${reference}`,
          text: fullText,
        });
      } catch {
        // User cancelled
      }
    } else {
      // Fallback to copy
      try {
        await navigator.clipboard.writeText(fullText);
        bibleState.showToast('Note copied to clipboard', 'success');
      } catch {
        // Ignore
      }
    }
  }, [location, noteText]);

  if (!shouldRender || !location) return null;

  const reference = formatReference(location);

  return (
    <div
      className={`note-editor-panel ${animationState === 'entering' ? 'entering' : ''} ${animationState === 'exiting' ? 'closing' : ''}`}
    >
      {/* Header */}
      <div className="note-editor-header">
        <div className="mb-2 flex items-start justify-between">
          <h3 className="text-xl font-semibold text-stone-800 dark:text-stone-200">Note</h3>
          <div className="flex items-center gap-2">
            {noteText.trim() && (
              <>
                <button
                  onClick={handleShare}
                  className="-m-1 rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-200"
                  title="Share"
                >
                  <ShareIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={handleDelete}
                  className="-m-1 rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-red-600 dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-red-400"
                  title="Delete"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </>
            )}
            <button
              onClick={handleClose}
              className="-m-1 rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-200"
              title="Close"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Verse reference header */}
        <button
          className="flex items-center gap-2 text-stone-600 transition-colors hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200"
          onClick={() => setIsShowingVerse(!isShowingVerse)}
        >
          <ChevronRightIcon
            className={`h-4 w-4 transition-transform ${isShowingVerse ? 'rotate-90' : ''}`}
          />
          <span className="font-medium">{reference}</span>
        </button>

        {isShowingVerse && verseText && (
          <p className="mt-2 text-sm leading-relaxed text-stone-700 dark:text-stone-300">
            {verseText}
          </p>
        )}
      </div>

      {/* Note input */}
      <div className="note-editor-content">
        <textarea
          ref={textareaRef}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Write your note here..."
          className="note-editor-textarea"
        />
      </div>
    </div>
  );
});

export default NoteEditor;
